import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GroupFieldsDialogData } from '../../../types/dialog.type';
import { MatIconModule } from '@angular/material/icon';
import {
  WcgButtonComponent,
  WcgDialogComponent,
  WcgInputFieldComponent,
  WcgUniSelectComponent,
} from '@wcg/frontend-framework';
import { CommonModule } from '@angular/common';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    WcgDialogComponent,
    WcgButtonComponent,
    WcgUniSelectComponent,
    ReactiveFormsModule,
    WcgInputFieldComponent,
  ],
  selector: 'wcg-group-fields-dialog',
  standalone: true,
  styleUrls: ['./group-fields.dialog.component.scss'],
  template: `
    <form [formGroup]="fieldsForm">
      <wcg-dialog [title]="data.title" [size]="data.size ?? 'small'">
        <div content class="w-full group-fields-dialog__content">
          @for (field of data.group.fields; track field) {
            <div class="mb-2">
              @switch (field.type) {
                @case ('string') {
                  <wcg-input-field
                    class="w-full"
                    [formControlName]="field.id"
                    [label]="field.label"
                    [hintText]="field.hintText ?? ''"
                    [errorText]="field.errorText ?? ''"
                    data-testid="single-text-dialog__input-field"
                  />
                }
                @case ('number') {
                  <wcg-input-field
                    class="w-full"
                    [formControlName]="field.id"
                    [label]="field.label"
                    [hintText]="field.hintText ?? ''"
                    [errorText]="field.errorText ?? ''"
                    type="number"
                    data-testid="single-text-dialog__input-field"
                  />
                }
                @case ('dropdown') {
                  <wcg-uni-select
                    class="w-full"
                    [options]="field.options || []"
                    [label]="field.label"
                    [formControlName]="field.id"
                  />
                }
              }
            </div>
          }
        </div>
        <div tertiary>
          <wcg-button color="tertiary" (click)="close()">Cancel</wcg-button>
        </div>
        <div primary>
          <wcg-button
            color="primary"
            [isDisabled]="fieldsForm.invalid"
            (click)="save()"
            >Save</wcg-button
          >
        </div>
      </wcg-dialog>
    </form>
  `,
})
export class GroupFieldsDialogComponent implements OnInit {
  public readonly data = inject<GroupFieldsDialogData>(MAT_DIALOG_DATA);  
  private readonly _dialogRef = inject( 
    MatDialogRef<GroupFieldsDialogComponent>, 
  );  
  private readonly _fb = inject(FormBuilder); 
  
  fieldsForm: FormGroup = this._fb.group({});

  ngOnInit(): void {
    const group: { [key: string]: FormControl } = {};
    for (const field of this.data.group.fields) {
      group[field.id] = this._createFormControl({
        ...field,
        isRequired: field.isRequired ?? false,
      });
    }
    this.fieldsForm = this._fb.group(group);
  }
  
  private _createFormControl(field: { value: unknown; isRequired: boolean }): FormControl {
    return new FormControl(
      field.value ?? '',
      field.isRequired ? [Validators.required] : [],
    );
  }

  
  close(): void {
    this._dialogRef.close();
  }

  save(): void {
    this._dialogRef.close(this.fieldsForm.value);
  }
}
